import type {
  RtiCalculationMethodologyOption,
  RtiCalculationMethodologyState,
} from 'model/interfaces/rti-template';

export type { RtiCalculationMethodologyOption, RtiCalculationMethodologyState };

export const METHODOLOGY_I18N_KEYS: Record<string, string> = {
  mean: 'pe.costPlanning.individualPayEquity.mean',
  median: 'pe.median',
};

export function formatMethodologyKeyToLabel(key: string): string {
  const trimmed = key.trim();
  if (!trimmed) return key;
  return trimmed
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(' ');
}

function isReservedMethodologyMetaKey(key: string): boolean {
  return key.startsWith('_');
}

function readBooleanFlag(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  return undefined;
}

function readEntryFromUnknown(
  key: string,
  value: unknown,
  labels: Record<string, string> | undefined,
): RtiCalculationMethodologyOption | null {
  if (isReservedMethodologyMetaKey(key)) return null;

  let checked = false;
  let label: string | undefined;

  if (typeof value === 'boolean') {
    checked = value;
  } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    const flag =
      readBooleanFlag(o.selected) ??
      readBooleanFlag(o.enabled) ??
      readBooleanFlag(o.checked);
    if (flag === undefined) return null;
    checked = flag;
    if (typeof o.label === 'string' && o.label.trim().length > 0) {
      label = o.label.trim();
    }
  } else {
    return null;
  }

  const resolvedLabel =
    label ?? (labels && typeof labels[key] === 'string' && labels[key]!.trim().length > 0
      ? labels[key]!.trim()
      : formatMethodologyKeyToLabel(key));

  return { key, label: resolvedLabel, checked };
}

/** Flat booleans sent on upsert (same contract as today). */
export function methodologyStateToPayload(state: RtiCalculationMethodologyState): Record<string, boolean> {
  return state.options.reduce<Record<string, boolean>>((acc, o) => {
    acc[o.key] = o.checked;
    return acc;
  }, {});
}

export function defaultCalculationMethodology(): RtiCalculationMethodologyState {
  return {
    options: [
      { key: 'mean', label: formatMethodologyKeyToLabel('mean'), checked: false },
      { key: 'median', label: formatMethodologyKeyToLabel('median'), checked: true },
    ],
  };
}

export function hasClientCalculationMethodologyConfig(cm: unknown): boolean {
  if (cm === null || typeof cm !== 'object' || Array.isArray(cm)) return false;
  const entries = Object.entries(cm as Record<string, unknown>).filter(([k]) => !isReservedMethodologyMetaKey(k));
  return entries.some(([, v]) => {
    if (typeof v === 'boolean') return true;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      return (
        readBooleanFlag(o.selected) !== undefined ||
        readBooleanFlag(o.enabled) !== undefined ||
        readBooleanFlag(o.checked) !== undefined
      );
    }
    return false;
  });
}

export function isCalculationMethodologySelectionValid(
  methodology: Record<string, boolean> | RtiCalculationMethodologyState,
): boolean {
  if (
    methodology !== null &&
    typeof methodology === 'object' &&
    'options' in methodology &&
    Array.isArray((methodology as RtiCalculationMethodologyState).options)
  ) {
    return (methodology as RtiCalculationMethodologyState).options.some((o) => o.checked === true);
  }
  return Object.values(methodology as Record<string, boolean>).some((v) => v === true);
}

export function normalizeCalculationMethodologyFromApi(
  cm: unknown,
  labels?: Record<string, string> | null,
): RtiCalculationMethodologyState {
  const labelMap = labels && typeof labels === 'object' ? labels : undefined;

  if (cm === null || typeof cm !== 'object' || Array.isArray(cm) || !hasClientCalculationMethodologyConfig(cm)) {
    return defaultCalculationMethodology();
  }

  const raw = cm as Record<string, unknown>;
  const options: RtiCalculationMethodologyOption[] = [];

  for (const [key, value] of Object.entries(raw)) {
    const opt = readEntryFromUnknown(key, value, labelMap);
    if (opt) options.push(opt);
  }

  if (options.length === 0) {
    return defaultCalculationMethodology();
  }

  const state: RtiCalculationMethodologyState = { options };
  if (!isCalculationMethodologySelectionValid(state)) {
    return defaultCalculationMethodology();
  }
  return state;
}

export function migratePersistedCalculationMethodology(raw: unknown): RtiCalculationMethodologyState {
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.options)) {
      const opts = o.options
        .filter(
          (item): item is Record<string, unknown> =>
            item !== null && typeof item === 'object' && !Array.isArray(item),
        )
        .map((item) => {
          const key = typeof item.key === 'string' ? item.key : '';
          if (!key) return null;
          const label =
            typeof item.label === 'string' && item.label.trim().length > 0
              ? item.label.trim()
              : formatMethodologyKeyToLabel(key);
          const checked = item.checked === true;
          return { key, label, checked };
        })
        .filter((x): x is RtiCalculationMethodologyOption => x !== null);
      if (opts.length > 0) {
        const migrated: RtiCalculationMethodologyState = { options: opts };
        if (!isCalculationMethodologySelectionValid(migrated)) {
          return defaultCalculationMethodology();
        }
        return migrated;
      }
    }
    if (typeof o.mean === 'boolean' || typeof o.median === 'boolean') {
      return normalizeCalculationMethodologyFromApi(
        { mean: o.mean, median: o.median },
        undefined,
      );
    }
  }
  return defaultCalculationMethodology();
}
